import React, { useState } from 'react';
import { Upload, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ChevronDown } from 'lucide-react';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const RedZoneReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.csv'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) || !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a CSV file only');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);

      // Send file to server for processing using the new generic endpoint
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/redzone`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTendencyFlag = (percentage) => {
    const pct = parseFloat(percentage);
    if (pct >= 80) return { flag: '❗', label: 'EXTREMELY VALUABLE', color: 'error' };
    if (pct >= 70) return { flag: '⚠️', label: 'VALUABLE', color: 'warning' };
    return { flag: '', label: '', color: 'default' };
  };

  const downloadPDF = async () => {
    if (!report) return;

    try {
      await generateStandardizedPDF(
        REPORT_SECTIONS.RED_ZONE_REPORT,
        'red-zone-report.pdf',
        setError
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF: ' + error.message);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to="/reports"
          startIcon={<ArrowLeft size={20} />}
          sx={{ mb: 2 }}
        >
          Back to Reports
        </Button>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FileText size={32} />
          Red Zone Report Generator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyze offensive tendencies in the red zone (6-20 yard line) on base downs (1st and 2nd down). Upload your CSV file containing play-by-play data.
        </Typography>
      </Box>

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Upload CSV Data</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a CSV file containing your play-by-play data. The file should include columns for down, distance, 
            run/pass, field position, personnel, and play results.
          </Typography>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="csv-file-upload"
            disabled={loading}
          />
          <label htmlFor="csv-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose CSV File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText size={20} />}
        >
          {loading ? 'Processing...' : 'Generate Report'}
        </Button>
        
        {(file || report) && (
          <Button variant="outlined" onClick={clearAll}>
            Clear All
          </Button>
        )}
      </Box>

      {/* Report Display */}
      {report && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                Red Zone Report Results
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<FileText size={20} />}
                onClick={downloadPDF}
              >
                Download PDF
              </Button>
            </Box>

            {/* Report Header */}
            <ReportHeader 
              title="RED ZONE ANALYSIS"
              fileName={file?.name}
              sectionId="report-header-section"
            />
            
            {/* Report Title */}
            <Typography 
              variant="h4" 
              align="center" 
              gutterBottom 
              data-section="title-section"
              sx={{ 
                fontWeight: 'bold', 
                color: '#000', 
                mb: 2,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto'
              }}
            >
              {report.title}
            </Typography>

            <Grid container spacing={3}>
              {/* Summary Data */}
              <Grid item xs={12}>
                <Card variant="outlined" data-section="summary-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Summary</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">Total Red Zone Plays</Typography>
                        <Typography variant="h6">{report.summary?.totalPlays || 0}</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">Personnel Groups</Typography>
                        <Typography variant="h6">{report.summary?.totalPersonnelGroups || 0}</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">Formations</Typography>
                        <Typography variant="h6">{report.summary?.totalFormations || 0}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Total Runs</Typography>
                        <Typography variant="h6">
                          {report.summary?.totalRuns || 0} ({report.summary?.runPercent || 0}%)
                          {getTendencyFlag(report.summary?.runPercent).flag && ` ${getTendencyFlag(report.summary?.runPercent).flag} ${getTendencyFlag(report.summary?.runPercent).label} RUN TENDENCY`}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Total Passes</Typography>
                        <Typography variant="h6">
                          {report.summary?.totalPasses || 0} ({report.summary?.passPercent || 0}%)
                          {getTendencyFlag(report.summary?.passPercent).flag && ` ${getTendencyFlag(report.summary?.passPercent).flag} ${getTendencyFlag(report.summary?.passPercent).label} PASS TENDENCY`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Personnel Usage */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" data-section="personnel-usage-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Personnel Usage in Red Zone</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Rank</TableCell>
                            <TableCell>Personnel</TableCell>
                            <TableCell align="right">Plays</TableCell>
                            <TableCell align="right">%</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.personnelUsage?.map((personnel) => (
                            <TableRow key={personnel.personnel}>
                              <TableCell>{personnel.rank}</TableCell>
                              <TableCell>{personnel.personnel}</TableCell>
                              <TableCell align="right">{personnel.plays}</TableCell>
                              <TableCell align="right">{personnel.percent}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Personnel Analysis */}
              <Grid item xs={12} md={8}>
                <Card variant="outlined" data-section="personnel-analysis-section">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Detailed Personnel Analysis</Typography>
                    
                    {report.personnelAnalysis?.map((personnel, index) => (
                      <Accordion key={personnel.personnel} defaultExpanded={index === 0}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {personnel.personnel} Personnel
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ({personnel.plays} plays - {personnel.totalPercent}% of red zone)
                              {getTendencyFlag(personnel.runPercent).flag && ` ${getTendencyFlag(personnel.runPercent).flag} ${getTendencyFlag(personnel.runPercent).label} RUN`}
                              {getTendencyFlag(personnel.passPercent).flag && ` ${getTendencyFlag(personnel.passPercent).flag} ${getTendencyFlag(personnel.passPercent).label} PASS`}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            {/* Run/Pass Summary */}
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Runs:</strong> {personnel.runs} ({personnel.runPercent}%) | 
                                <strong> Passes:</strong> {personnel.passes} ({personnel.passPercent}%)
                              </Typography>
                            </Grid>

                            {/* Formations */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Top Formations:</Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Formation</TableCell>
                                      <TableCell align="right">Plays</TableCell>
                                      <TableCell align="right">Run%</TableCell>
                                      <TableCell align="right">Pass%</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {personnel.formations?.slice(0, 5).map((formation) => (
                                      <TableRow key={formation.formation}>
                                        <TableCell>{formation.formation || 'Unknown'}</TableCell>
                                        <TableCell align="right">{formation.plays}</TableCell>
                                        <TableCell align="right">
                                          {formation.runPercent}%
                                          {getTendencyFlag(formation.runPercent).flag}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formation.passPercent}%
                                          {getTendencyFlag(formation.passPercent).flag}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>

                            {/* Run Concepts */}
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                Run Concepts ({personnel.totalRunPlays} total run plays):
                              </Typography>
                              {personnel.runConcepts?.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Concept</TableCell>
                                        <TableCell align="right">Count</TableCell>
                                        <TableCell align="right">%</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {personnel.runConcepts.map((concept) => (
                                        <TableRow key={concept.concept}>
                                          <TableCell>{concept.concept}</TableCell>
                                          <TableCell align="right">{concept.count}</TableCell>
                                          <TableCell align="right">{concept.percent}%</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No run plays in this personnel grouping
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </CardContent>
                </Card>
              </Grid>

              {/* Other Personnel */}
              {report.otherPersonnel?.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" data-section="other-personnel-section">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Other Personnel Groupings</Typography>
                      <Grid container spacing={2}>
                        {report.otherPersonnel.map((personnel) => (
                          <Grid item xs={12} md={6} lg={4} key={personnel.personnel}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {personnel.personnel} Personnel ({personnel.plays} plays)
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                  {getTendencyFlag(personnel.runPercent).flag ? (
                                    <Typography variant="body2">
                                      <strong>Runs:</strong> {personnel.runs} ({personnel.runPercent}%) {getTendencyFlag(personnel.runPercent).flag} {getTendencyFlag(personnel.runPercent).label} RUN TENDENCY
                                    </Typography>
                                  ) : getTendencyFlag(personnel.passPercent).flag ? (
                                    <Typography variant="body2">
                                      <strong>Passes:</strong> {personnel.passes} ({personnel.passPercent}%) {getTendencyFlag(personnel.passPercent).flag} {getTendencyFlag(personnel.passPercent).label} PASS TENDENCY
                                    </Typography>
                                  ) : (
                                    <>
                                      <Typography variant="body2"><strong>Runs:</strong> {personnel.runs} ({personnel.runPercent}%)</Typography>
                                      <Typography variant="body2"><strong>Passes:</strong> {personnel.passes} ({personnel.passPercent}%)</Typography>
                                    </>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RedZoneReportGenerator;